import type { api } from "@healthos/schemas";
import { client, jsonOrThrow } from "../api/healthos";

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

function ensureNotificationServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return Promise.resolve(null);
  }
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => reg)
      .catch(() => null);
  }
  return registrationPromise;
}

async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === "undefined") return "denied";
  if (Notification.permission === "granted") {
    void ensureNotificationServiceWorker();
    return "granted";
  }
  if (Notification.permission === "denied") return "denied";
  try {
    const result = await Notification.requestPermission();
    if (result === "granted") void ensureNotificationServiceWorker();
    return result;
  } catch {
    return "denied";
  }
}

export type WebPushRegistrationStatus =
  | "unsupported"
  | "permission_denied"
  | "not_configured"
  | "subscribed"
  | "failed";

export async function enableWebPushNotifications(): Promise<{
  permission: NotificationPermission;
  status: WebPushRegistrationStatus;
}> {
  const permission = await requestNotificationPermission();
  if (permission !== "granted") return { permission, status: "permission_denied" };
  return { permission, status: await syncWebPushSubscription() };
}

export async function syncWebPushSubscription(): Promise<WebPushRegistrationStatus> {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return "permission_denied";
  }
  const reg = await ensureNotificationServiceWorker();
  if (!reg || !("pushManager" in reg)) return "unsupported";

  try {
    const config = await jsonOrThrow<api.WebPushPublicKeyResponse>(
      await client().api.notifications["web-push"]["public-key"].$get(),
      "GET /api/notifications/web-push/public-key",
    );
    if (!config.configured || !config.publicKey) return "not_configured";

    const applicationServerKey = vapidPublicKeyToArrayBuffer(config.publicKey);
    const existing = await reg.pushManager.getSubscription();
    if (existing && !subscriptionMatchesKey(existing, applicationServerKey)) {
      await existing.unsubscribe();
    }
    const current = await reg.pushManager.getSubscription();
    const subscription =
      current ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      }));
    const body = subscriptionToRequest(subscription);
    if (!body) return "failed";
    await jsonOrThrow<api.RegisterWebPushSubscriptionResponse>(
      await client().api.notifications["web-push"].subscriptions.$post({ json: body }),
      "POST /api/notifications/web-push/subscriptions",
    );
    return "subscribed";
  } catch (err) {
    console.warn("web_push_subscription_failed", err);
    return "failed";
  }
}

export async function disableWebPushNotifications(): Promise<void> {
  const reg = await ensureNotificationServiceWorker();
  if (!reg || !("pushManager" in reg)) return;
  const subscription = await reg.pushManager.getSubscription();
  if (!subscription) return;
  await client()
    .api.notifications["web-push"].subscriptions.$delete({
      json: { endpoint: subscription.endpoint },
    })
    .catch((err) => console.warn("web_push_unsubscribe_server_failed", err));
  await subscription
    .unsubscribe()
    .catch((err) => console.warn("web_push_unsubscribe_browser_failed", err));
}

function subscriptionToRequest(
  subscription: PushSubscription,
): api.RegisterWebPushSubscriptionRequest | null {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return null;
  return {
    endpoint: json.endpoint,
    expirationTime: json.expirationTime ?? null,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
  };
}

function vapidPublicKeyToArrayBuffer(publicKey: string): ArrayBuffer {
  const padding = "=".repeat((4 - (publicKey.length % 4)) % 4);
  const base64 = `${publicKey}${padding}`.replaceAll("-", "+").replaceAll("_", "/");
  const raw = window.atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i);
  }
  const buffer = new ArrayBuffer(bytes.length);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

function subscriptionMatchesKey(
  subscription: PushSubscription,
  applicationServerKey: ArrayBuffer,
): boolean {
  const existing = subscription.options.applicationServerKey;
  if (!existing) return false;
  const left = bufferSourceToUint8Array(existing);
  const right = new Uint8Array(applicationServerKey);
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === (right[index] ?? -1));
}

function bufferSourceToUint8Array(source: BufferSource): Uint8Array {
  if (source instanceof ArrayBuffer) return new Uint8Array(source);
  return new Uint8Array(source.buffer, source.byteOffset, source.byteLength);
}
